import React from "react";
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FaMedal, FaTrophy } from 'react-icons/fa';
import { MdEmojiEvents } from 'react-icons/md';
import { GiLaurelCrown } from 'react-icons/gi';

interface Badge {
  id: number;
  name: string;
  description?: string;
  image_path?: string;
  badge_type?: string;
  is_active?: boolean;
  awarded_at?: string;
  is_showcased?: boolean;
}

interface MyBadgesProps extends RouteComponentProps {
  badges?: Badge[];
  loading?: boolean;
}

const badgeIcons = [FaMedal, FaTrophy, MdEmojiEvents, GiLaurelCrown];

const MyBadges: React.FC<MyBadgesProps> = (props) => {
  const { badges = [], loading = false } = props;
  const [imageErrors, setImageErrors] = React.useState<Set<number>>(new Set());

  // Calculate earned badges
  const earned = Array.isArray(badges) ? badges.length : 0;
  const total = 15; // This could also come from props if you want to make it dynamic

  const handleImageError = (badgeId: number) => {
    setImageErrors(prev => new Set(prev).add(badgeId));
  };

  const handleClick = () => {
    (props as any).history.push('/badges');
  };

  return (
    <div
      onClick={handleClick}
      style={{
        width: '100%',
        height: 'auto',
        background: "#fff",
        borderRadius: 19,
        boxShadow: "0px 4px 4px rgba(0,0,0,0.25)",
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        textAlign: "center",
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0px 6px 12px rgba(0,0,0,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0px)";
        e.currentTarget.style.boxShadow = "0px 4px 4px rgba(0,0,0,0.25)";
      }}
    >
      {/* Title and View More */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginLeft: 16, marginTop: 8}}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <div style={{ fontFamily: "SF Pro Text", fontWeight: 700, fontSize: 18, lineHeight: "28px", letterSpacing: 0.875, color: "#000" }}>
            <h3 style={{fontWeight:'bold',fontStyle:'italic', margin: 0}}>My Badges</h3>
          </div>
          <div style={{ fontFamily: "SF Pro Text", fontWeight: 600, fontSize: 20, color: "#737373", marginTop: 4 }}>
            {loading ? "Loading..." : `Earned ${earned} of ${total}`}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 16 }}>
          <span style={{ fontWeight: 600, fontSize: 20, fontStyle: 'italic', color: "#18998E" }}>
            View More
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            background: '#fff',
            border: '2px solid #18998E',
            borderRadius: '50%',
            marginLeft: 4
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="#18998E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>
      {/* Divider */}
      <div style={{ width: '100%', height: 0, marginLeft: 0, marginTop: 7, borderTop: "1px solid #ECECEC" }} />
      {/* Badges Row */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          marginTop: 16,
          padding: "20px",
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          height: '100%',
          gap: 16,
        }}
      >
        {loading ? (
          <div style={{ width: '100%', textAlign: 'center', padding: '20px' }}>
            Loading badges...
          </div>
        ) : !Array.isArray(badges) || badges.length === 0 ? (
          <div style={{ width: '100%', textAlign: 'center', padding: '20px', color: '#757575' }}>
            No badges earned yet. Keep participating to earn your first badge!
          </div>
        ) : (
          badges.slice(0, 7).map((badge, idx) => {
            const Icon = badgeIcons[idx % badgeIcons.length];
            const awardedDate = badge.awarded_at ? new Date(badge.awarded_at).toLocaleDateString() : 'Recently';
            const hasImageError = imageErrors.has(badge.id);
            
            return (
              <div key={badge.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 98, minHeight: 120, gap: 8 }}>
                {badge.image_path && !hasImageError ? (
                  <img 
                    src={badge.image_path} 
                    alt={badge.name || 'Badge'}
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '50%' }}
                    onError={() => handleImageError(badge.id)}
                  />
                ) : (
                  <Icon size={64} color="#FFD700" />
                )}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", height: 64, textAlign: "center" }}>
                  <div
                    style={{
                      fontFamily: "SF Pro Text",
                      fontWeight: 600,
                      fontSize: 12,
                      lineHeight: "16px",
                      letterSpacing: 0.5,
                      color: "#000",
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      overflow: "hidden",
                      wordWrap: "break-word",
                      marginBottom: 2,
                    }}
                  >
                    {badge.name || 'Badge'}
                  </div>
                  <span
                    style={{
                      fontFamily: "SF Pro Text",
                      fontWeight: 400,
                      fontSize: 10,
                      lineHeight: "14px",
                      letterSpacing: 0.3,
                      color: "#757575",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Earned {awardedDate}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default withRouter(MyBadges); 